/**
 * Configurazione per il componente WorkOrderDownloadMenu
 * Permette di configurare il comportamento del menu di download per i WorkOrder
 *
 * Questa configurazione può essere sovrascritta tramite il sistema di override del CtManagerService
 */
export class WorkOrderDownloadMenuConfiguration {

  /**
   * Abilita/disabilita il menu di download avanzato
   * Se false, viene utilizzato il componente legacy lang-download-file
   * Default: true
   */
  enabled: boolean = true;

  /**
   * Numero massimo di documenti da mostrare dalla tab "Gestione Documenti"
   * I documenti vengono ordinati per data di creazione (più recenti prima)
   * Default: 5
   */
  maxDocumentsToShow: number = 5;

  /**
   * Icona del bottone (Material Icon)
   * Default: 'picture_as_pdf'
   */
  icon: string = 'picture_as_pdf';

  /**
   * Colore del bottone (Material color palette: primary, accent, warn)
   * Default: 'primary'
   */
  color: string = 'primary';

  /**
   * Tipo di bottone (mat-icon-button o mat-mini-fab)
   * Default: 'mat-icon-button'
   */
  buttonType: 'mat-icon-button' | 'mat-mini-fab' = 'mat-icon-button';

  /**
   * Etichetta per il PDF generato automaticamente nel menu
   * Default: 'PDF Generato Automaticamente'
   */
  generatedPdfLabel: string = 'PDF Generato Automaticamente';

  /**
   * Etichetta per la sezione dei documenti caricati
   * Default: 'Documenti Caricati'
   */
  uploadedDocumentsLabel: string = 'Documenti Caricati';

  /**
   * Mostra/nascondi l'icona del file accanto al nome del documento
   * Default: true
   */
  showFileIcon: boolean = true;

  /**
   * Abilita il raggruppamento delle lingue per tipologia di documento (Preventivo / Conferma d'Ordine).
   * Quando true, il menu mostra due voci di primo livello ("Preventivo", "Conferma d'Ordine")
   * con un sotto-menu annidato che contiene le lingue.
   * Quando false, le lingue sono mostrate come voci flat (comportamento storico).
   * Default: false
   */
  groupByDocumentType: boolean = false;

  /**
   * Etichetta della voce di raggruppamento "Preventivo" (usata solo se groupByDocumentType = true).
   * Default: 'Preventivo'
   */
  quotationGroupLabel: string = 'Preventivo';

  /**
   * Etichetta della voce di raggruppamento "Conferma d'Ordine" (usata solo se groupByDocumentType = true).
   * Default: "Conferma d'Ordine"
   */
  confirmationGroupLabel: string = "Conferma d'Ordine";

  /**
   * Lingue disponibili per il PDF generato
   * Ogni lingua apparirà come voce separata nel menu principale con la propria bandiera
   * Stesso formato del componente legacy lang-download-file
   * Default: Solo Italiano
   */
  languages: Array<LanguageOption> = [
    {
      language: "Italiano",
      code: "it",
      type: "IT",
      icon: "it",
      rowType: WorkOrderRowsExportTypeVO.All
    }
  ];
    /**
   * Elenco di template PDF custom (es. DTS: Informativa/Modulo/Registro).
   * Quando valorizzato, il menu mostra SOLO queste voci e ignora lingue/gruppi.
   * Vuoto (default) = comportamento storico invariato per gli altri clienti.
   */
  exportTemplates: Array<ExportTemplateOption> = [];

  private constructor() {}

  /**
   * Metodo factory per creare una nuova istanza della configurazione
   */
  public static create(): WorkOrderDownloadMenuConfiguration {
    return new WorkOrderDownloadMenuConfiguration();
  }

  /**
   * Imposta se il menu è abilitato
   */
  setEnabled(value: boolean): WorkOrderDownloadMenuConfiguration {
    this.enabled = value;
    return this;
  }

  /**
   * Imposta il numero massimo di documenti da mostrare
   */
  setMaxDocumentsToShow(value: number): WorkOrderDownloadMenuConfiguration {
    this.maxDocumentsToShow = value;
    return this;
  }

  /**
   * Imposta l'icona del bottone
   */
  setIcon(value: string): WorkOrderDownloadMenuConfiguration {
    this.icon = value;
    return this;
  }

  /**
   * Imposta il colore del bottone
   */
  setColor(value: string): WorkOrderDownloadMenuConfiguration {
    this.color = value;
    return this;
  }

  /**
   * Imposta il tipo di bottone
   */
  setButtonType(value: 'mat-icon-button' | 'mat-mini-fab'): WorkOrderDownloadMenuConfiguration {
    this.buttonType = value;
    return this;
  }

  /**
   * Imposta l'etichetta per il PDF generato
   */
  setGeneratedPdfLabel(value: string): WorkOrderDownloadMenuConfiguration {
    this.generatedPdfLabel = value;
    return this;
  }

  /**
   * Imposta l'etichetta per i documenti caricati
   */
  setUploadedDocumentsLabel(value: string): WorkOrderDownloadMenuConfiguration {
    this.uploadedDocumentsLabel = value;
    return this;
  }

  /**
   * Imposta la visibilità dell'icona del file
   */
  setShowFileIcon(value: boolean): WorkOrderDownloadMenuConfiguration {
    this.showFileIcon = value;
    return this;
  }

  /**
   * Abilita il raggruppamento delle lingue per tipologia di documento (Preventivo / Conferma d'Ordine).
   */
  setGroupByDocumentType(value: boolean): WorkOrderDownloadMenuConfiguration {
    this.groupByDocumentType = value;
    return this;
  }

  /**
   * Imposta l'etichetta del gruppo "Preventivo".
   */
  setQuotationGroupLabel(value: string): WorkOrderDownloadMenuConfiguration {
    this.quotationGroupLabel = value;
    return this;
  }

  /**
   * Imposta l'etichetta del gruppo "Conferma d'Ordine".
   */
  setConfirmationGroupLabel(value: string): WorkOrderDownloadMenuConfiguration {
    this.confirmationGroupLabel = value;
    return this;
  }

  /**
   * Imposta le lingue disponibili per il PDF generato
   * Ogni lingua apparirà come voce separata nel menu con la propria bandiera
   */
  setLanguages(value: Array<LanguageOption>): WorkOrderDownloadMenuConfiguration {
    this.languages = value;
    return this;
  }
 setExportTemplates(value: Array<ExportTemplateOption>): WorkOrderDownloadMenuConfiguration {
    this.exportTemplates = value;
    return this;
  }

}

/**
 * Interfaccia per le opzioni di lingua
 * Compatibile con il formato del componente legacy lang-download-file
 */
export interface LanguageOption {
  language: string;
  code: string;
  type: string;
  icon: string;
  rowType?: WorkOrderRowsExportTypeVO;
}

export interface ExportTemplateOption {
  label: string;
  exportCustomTemplateType: number;
  icon?: string;
  languageCode?: string;
  rowType?: WorkOrderRowsExportTypeVO;
}

/**
 * Enum per definire quali righe esportare nel PDF del WorkOrder
 * Copiato da lang-download-file.configuration.ts per mantenere compatibilità
 */
export enum WorkOrderRowsExportTypeVO {
  All = 1,
  IsSelfOnly = 2,
  NotIsSelfOnly = 3
}
